// src/hooks/useTracking.js (CORREGIDO COMPLETAMENTE)
import { useState, useCallback, useEffect, useRef } from 'react';
import trackingService from '../services/trackingService';

export const useTracking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [currentStep, setCurrentStep] = useState('search');
  const [authType, setAuthType] = useState(null);
  const [pendingTrackingNumber, setPendingTrackingNumber] = useState('');
  const [preAuthData, setPreAuthData] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [hasProcessedUrlTracking, setHasProcessedUrlTracking] = useState(false);

  const refreshIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  // ✅ Limpiar al desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // ✅ FUNCIÓN: Configurar intervalo de refresco
  const setupRefreshInterval = useCallback((trackingNumber) => {
    // Limpiar intervalo existente
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Crear nuevo intervalo (cada 60 minutos)
    refreshIntervalRef.current = setInterval(
      async () => {
        console.log('🔄 Refrescando datos automáticamente (60 minutos)...');

        try {
          // Buscar datos de autenticación guardados
          const authKey = `tracking_auth_${trackingNumber}`;
          const savedAuthData = localStorage.getItem(authKey);

          if (savedAuthData) {
            const parsedData = JSON.parse(savedAuthData);

            // Verificar que no haya expirado
            const now = new Date().getTime();
            if (
              parsedData.expiresAt &&
              parsedData.expiresAt > now &&
              parsedData.preAuthData
            ) {
              // Hacer refresh de los datos
              const result = await trackingService.completeAuth({
                sr_number: parsedData.preAuthData.sr_number,
                auth_value: parsedData.authValue,
                auth_type: parsedData.preAuthData.auth_type,
              });

              if (result.success && isMountedRef.current) {
                console.log('✅ Datos actualizados automáticamente');

                // Actualizar estado
                setTrackingData(result.data);

                // Actualizar localStorage
                localStorage.setItem(
                  `tracking_results_${trackingNumber}`,
                  JSON.stringify({
                    data: result.data,
                    timestamp: new Date().getTime(),
                    expiresAt: new Date().getTime() + 60 * 60 * 1000,
                  })
                );
              }
            }
          }
        } catch (err) {
          console.error('❌ Error en refresh automático:', err);
        }
      },
      60 * 60 * 1000
    ); // 60 minutos

    console.log('⏰ Intervalo de refresco configurado para 60 minutos');
  }, []);

  // ✅ FUNCIÓN: Cargar datos guardados
  const loadSavedData = useCallback((trackingNumber) => {
    try {
      console.log('🔍 Buscando datos guardados para:', trackingNumber);

      // Buscar resultados guardados
      const resultsKey = `tracking_results_${trackingNumber}`;
      const savedResults = localStorage.getItem(resultsKey);

      if (savedResults) {
        const parsedResults = JSON.parse(savedResults);
        const now = new Date().getTime();

        // Verificar si no ha expirado
        if (parsedResults.expiresAt && parsedResults.expiresAt > now) {
          console.log('📂 Cargando resultados guardados desde localStorage');
          return {
            type: 'results',
            data: parsedResults.data,
            timestamp: parsedResults.timestamp,
          };
        } else {
          // Eliminar resultados expirados
          console.log('🗑️ Eliminando resultados expirados');
          localStorage.removeItem(resultsKey);
        }
      }

      // Buscar datos de autenticación guardados
      const authKey = `tracking_auth_${trackingNumber}`;
      const savedAuthData = localStorage.getItem(authKey);

      if (savedAuthData) {
        const parsedAuthData = JSON.parse(savedAuthData);
        const now = new Date().getTime();

        // Verificar si no ha expirado
        if (parsedAuthData.expiresAt && parsedAuthData.expiresAt > now) {
          console.log('🔐 Cargando datos de autenticación guardados');
          return {
            type: 'auth',
            data: parsedAuthData,
          };
        } else {
          // Eliminar auth expirada
          console.log('🗑️ Eliminando autenticación expirada');
          localStorage.removeItem(authKey);
        }
      }

      console.log('📭 No hay datos guardados para:', trackingNumber);
    } catch (error) {
      console.error('❌ Error loading saved data:', error);
    }

    return null;
  }, []);

  // ✅ FUNCIÓN: Guardar resultados
  const saveResults = useCallback((trackingNumber, data) => {
    if (trackingNumber && data) {
      try {
        const resultsData = {
          data: data,
          timestamp: new Date().getTime(),
          expiresAt: new Date().getTime() + 60 * 60 * 1000,
        };

        localStorage.setItem(
          `tracking_results_${trackingNumber}`,
          JSON.stringify(resultsData)
        );
        console.log('✅ Resultados guardados en localStorage');
      } catch (error) {
        console.error('Error saving results:', error);
      }
    }
  }, []);

  // ✅ FUNCIÓN: Guardar datos de autenticación
  const saveAuthData = useCallback((trackingNumber, authValue, preAuthData) => {
    if (trackingNumber && preAuthData) {
      try {
        const authData = {
          authValue,
          preAuthData: preAuthData,
          timestamp: new Date().getTime(),
          expiresAt: new Date().getTime() + 60 * 60 * 1000,
        };

        localStorage.setItem(
          `tracking_auth_${trackingNumber}`,
          JSON.stringify(authData)
        );
        console.log('✅ Datos de autenticación guardados en localStorage');
      } catch (error) {
        console.error('Error saving auth data:', error);
      }
    }
  }, []);

  // ✅ FUNCIÓN PRINCIPAL: Buscar tracking (con autenticación)
  const searchTracking = useCallback(
    async (authValue) => {
      if (!preAuthData || !pendingTrackingNumber) {
        setError('No hay información de pre-autenticación disponible');
        return;
      }

      setLoading(true);
      setError(null);
      setLoadingMessage('Verificando credenciales y obteniendo información...');

      try {
        console.log('🔐 Completando autenticación:', {
          sr_number: preAuthData.sr_number,
          auth_value: authValue,
          auth_type: preAuthData.auth_type,
        });

        const result = await trackingService.completeAuth({
          sr_number: preAuthData.sr_number,
          auth_value: authValue,
          auth_type: preAuthData.auth_type,
        });

        console.log('✅ Resultado autenticación completa:', result);

        if (result.success) {
          // Actualizar estado
          setTrackingData(result.data);
          setCurrentStep('results');

          // Guardar datos
          saveResults(pendingTrackingNumber, result.data);

          // Guardar datos de autenticación si se proporcionó
          if (authValue && authValue.trim() !== '') {
            saveAuthData(pendingTrackingNumber, authValue, preAuthData);
          }

          // Configurar intervalo de refresco
          setupRefreshInterval(pendingTrackingNumber);
        } else {
          setError(
            result.error || 'Error al obtener la información del tracking'
          );
        }
      } catch (err) {
        console.error('❌ Error en auth completa:', err);
        setError(err.message || 'Error de conexión con el servidor');
      } finally {
        setLoading(false);
        setLoadingMessage('');
      }
    },
    [
      preAuthData,
      pendingTrackingNumber,
      saveResults,
      saveAuthData,
      setupRefreshInterval,
    ]
  );

  // ✅ FUNCIÓN: Procesar número de tracking (desde URL o búsqueda)
  const requestAuth = useCallback(
    async (trackingNumber, isFromUrl = false) => {
      console.log('📝 requestAuth llamado:', {
        trackingNumber,
        isFromUrl,
        currentStep,
      });

      // ✅ NUEVO: SIEMPRE verificar cache primero para mostrar resultados inmediatos
      const savedData = loadSavedData(trackingNumber);

      if (savedData?.type === 'results') {
        console.log('⚡ Mostrando resultados cacheados inmediatamente');

        // 1. MOSTRAR RESULTADOS CACHEADOS INMEDIATAMENTE
        setTrackingData(savedData.data);
        setPendingTrackingNumber(trackingNumber);
        setCurrentStep('results');
        setupRefreshInterval(trackingNumber);

        // 2. EN SEGUNDO PLANO: Actualizar datos
        setLoadingMessage('Actualizando información...');
        setLoading(true);

        try {
          // Buscar datos de autenticación para actualizar
          const authKey = `tracking_auth_${trackingNumber}`;
          const savedAuthData = localStorage.getItem(authKey);

          if (savedAuthData) {
            const parsedAuthData = JSON.parse(savedAuthData);
            const result = await trackingService.completeAuth({
              sr_number: parsedAuthData.preAuthData.sr_number,
              auth_value: parsedAuthData.authValue,
              auth_type: parsedAuthData.preAuthData.auth_type,
            });

            if (result.success) {
              console.log('✅ Datos actualizados en segundo plano');
              setTrackingData(result.data);
              saveResults(trackingNumber, result.data);
            }
          }
        } catch (err) {
          console.log('⚠️ No se pudieron actualizar los datos:', err);
        } finally {
          setLoading(false);
          setLoadingMessage('');
        }

        return; // Salir, ya mostramos cache
      }

      // Resto del código original (para cuando NO hay cache)...
      if (savedData?.type === 'auth') {
        // Tenemos datos de autenticación, obtener resultados actualizados
        setPreAuthData(savedData.data.preAuthData);
        setAuthType(savedData.data.preAuthData.auth_type);
        setPendingTrackingNumber(trackingNumber);

        // Obtener resultados actualizados
        await searchTracking(savedData.data.authValue);
        return;
      }

      // ✅ Paso 3: No hay datos guardados, iniciar flujo normal
      setLoading(true);
      setError(null);
      setLoadingMessage('Verificando número de tracking...');
      setPendingTrackingNumber(trackingNumber);

      try {
        console.log('🔍 Solicitando pre-auth para:', trackingNumber);

        const result = await trackingService.getPreAuthInfo(trackingNumber);

        console.log('📋 Respuesta pre-auth:', result);

        if (result.success) {
          // ✅ VALIDACIÓN: Verificar si categoria_c es válida
          const categoriaC = result.data?.categoria_c;
          const categoriasPermitidas = ['1', '7', '8'];

          if (categoriaC && !categoriasPermitidas.includes(categoriaC)) {
            setError(
              'El número de seguimiento que ingresaste no es de un pedido.'
            );
            setLoading(false);
            setLoadingMessage('');
            return;
          }

          setPreAuthData(result.data);
          setAuthType(result.data.auth_type);

          if (!result.data.requires_auth) {
            setLoadingMessage('Obteniendo información del pedido...');
            await searchTracking('');
          } else {
            setCurrentStep('auth');
          }
        } else {
          setError(result.error || 'Error al verificar el número de tracking');
        }
      } catch (err) {
        console.error('❌ Error en pre-auth:', err);
        setError(err.message || 'Error de conexión con el servidor');
      } finally {
        setLoading(false);
        setLoadingMessage('');
      }
    },
    [
      loading,
      pendingTrackingNumber,
      currentStep,
      trackingData,
      loadSavedData,
      searchTracking,
      setupRefreshInterval,
      saveResults,
    ]
  );

  // ✅ FUNCIÓN: Resetear tracking COMPLETAMENTE
  const resetTracking = useCallback(() => {
    console.log('🔄 Reseteando visualización...');

    // Limpiar intervalo de refresco
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // ✅ IMPORTANTE: NO limpiar localStorage aquí
    // Solo resetear estado visual

    // Resetear estado
    setTrackingData(null);
    setError(null);
    setAuthType(null);
    setPendingTrackingNumber('');
    setPreAuthData(null);
    setLoadingMessage('');
    setCurrentStep('search');
    setHasProcessedUrlTracking(false);
  }, []);

  const clearPendingData = useCallback(() => {
    console.log('🧹 Limpiando datos pendientes...');
    setPendingTrackingNumber('');
    setPreAuthData(null);
    setAuthType(null);
  }, []);

  // ✅ FUNCIÓN: Resetear para NUEVA búsqueda (limpia todo)
  const resetForNewSearch = useCallback(() => {
    console.log('🧹 Reseteo completo para nueva sesión...');

    // Limpiar intervalo
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // ✅ SOLO limpiar localStorage si el usuario explícitamente cierra sesión
    // OPCIONAL: clearAllSavedData(); // Comentado para preservar cache

    // Resetear estado
    setTrackingData(null);
    setError(null);
    setAuthType(null);
    setPendingTrackingNumber('');
    setPreAuthData(null);
    setLoadingMessage('');
    setCurrentStep('search');
    setHasProcessedUrlTracking(false);
  }, []);

  const markUrlTrackingAsProcessed = useCallback(() => {
    setHasProcessedUrlTracking(true);
  }, []);

  return {
    loading,
    error,
    trackingData,
    currentStep,
    authType,
    pendingTrackingNumber,
    preAuthData,
    loadingMessage,
    hasProcessedUrlTracking,
    searchTracking,
    requestAuth,
    resetTracking,
    resetForNewSearch, // ✅ Nueva función para nueva búsqueda
    markUrlTrackingAsProcessed,
    setCurrentStep,
    setTrackingData,
    loadSavedData, // ✅ Exportar para debugging
  };
};
